import { MapPin, CalendarCheck, BotMessageSquare, Bell } from 'lucide-react';
import { studentFeatures } from '@/mocks/homeData';

const iconMap: Record<string, React.ReactNode> = {
  MapPin: <MapPin className="w-6 h-6 text-gold-500" />,
  CalendarCheck: <CalendarCheck className="w-6 h-6 text-gold-500" />,
  BotMessageSquare: <BotMessageSquare className="w-6 h-6 text-gold-500" />,
  Bell: <Bell className="w-6 h-6 text-gold-500" />,
};

export default function ForStudentsSection() {
  return (
    <section id="for-students" className="bg-navy-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <div>
              <span className="text-xs font-semibold tracking-widest text-gold-600 uppercase">For Students</span>
              <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-navy-600">
                Study Abroad in Your Pocket
              </h2>
              <p className="mt-4 text-base md:text-lg text-gray-600 leading-relaxed">
                Everything you need to plan, apply, and succeed abroad — right from your phone.
              </p>

              <div className="mt-8 space-y-6">
                {studentFeatures.map((feature) => (
                  <div key={feature.title} className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                      {iconMap[feature.icon]}
                    </div>
                    <div>
                      <h3 className="text-base md:text-lg font-semibold text-navy-600">{feature.title}</h3>
                      <p className="mt-1 text-sm text-gray-500 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* App Download Buttons */}
              <div className="mt-10 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 px-5 py-3 bg-navy-100 text-navy-500 rounded-lg" aria-disabled="true">
                  <i className="ri-apple-fill text-xl" />
                  <div className="text-left">
                    <div className="text-[10px] leading-none opacity-80">Planned for</div>
                    <div className="text-sm font-semibold leading-tight">App Store</div>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 px-5 py-3 bg-navy-100 text-navy-500 rounded-lg" aria-disabled="true">
                  <i className="ri-google-play-fill text-xl" />
                  <div className="text-left">
                    <div className="text-[10px] leading-none opacity-80">Planned for</div>
                    <div className="text-sm font-semibold leading-tight">Google Play</div>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-xs text-gray-400">
                Student mobile apps are planned for a later phase.
              </p>
            </div>

            {/* Right: Phone Mockup */}
            <div className="relative flex justify-center">
              <div className="relative w-64 md:w-72 lg:w-80">
                {/* Phone frame */}
                <div className="bg-navy-700 rounded-[2.5rem] p-3 shadow-2xl">
                  <div className="bg-white rounded-[2rem] overflow-hidden">
                    {/* Notch */}
                    <div className="h-6 bg-navy-700 mx-auto w-24 rounded-b-xl" />
                    {/* Screen content */}
                    <div className="p-4 space-y-3">
                      <div className="h-8 bg-navy-50 rounded-lg flex items-center px-3">
                        <i className="ri-search-line text-gray-400 text-sm" />
                        <span className="text-xs text-gray-400 ml-2">Search consultancies...</span>
                      </div>
                      <div className="h-20 bg-gradient-to-r from-navy-600 to-navy-500 rounded-xl p-3">
                        <div className="text-white text-xs font-semibold">Welcome to EzyAbroad</div>
                        <div className="text-white/70 text-[10px] mt-1">Find your dream university</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="h-16 bg-gray-50 rounded-lg p-2">
                          <div className="w-6 h-6 bg-gold-100 rounded-full flex items-center justify-center">
                            <i className="ri-map-pin-line text-gold-500 text-xs" />
                          </div>
                          <div className="text-[10px] text-gray-600 mt-1">Nearby</div>
                        </div>
                        <div className="h-16 bg-gray-50 rounded-lg p-2">
                          <div className="w-6 h-6 bg-gold-100 rounded-full flex items-center justify-center">
                            <i className="ri-calendar-line text-gold-600 text-xs" />
                          </div>
                          <div className="text-[10px] text-gray-600 mt-1">Bookings</div>
                        </div>
                      </div>
                      <div className="h-12 bg-gray-50 rounded-lg flex items-center px-3 gap-2">
                        <div className="w-8 h-8 bg-navy-100 rounded-full flex items-center justify-center">
                          <i className="ri-robot-line text-navy-500 text-xs" />
                        </div>
                        <div className="text-[10px] text-gray-600">AI Assistant ready to help</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating badge */}
                <div className="absolute -right-4 top-12 bg-white rounded-lg shadow-lg px-3 py-2 flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-500 text-sm" />
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-navy-600">Appointment</div>
                    <div className="text-[9px] text-gray-400">Confirmed!</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
